<template>
  <div class="app-shell">
    <div class="app-topbar">
      <div class="app-topbar__inner">
        <span class="app-topbar__text">Simplify your sourcing workflow with one platform</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
          stroke-linejoin="round" class="app-topbar__arrow">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </div>
    </div>
    <header class="app-header" :class="{ 'is-scrolled': scrolled }">
      <div class="app-header__inner">
        <router-link :to="{ name: 'Home' }" class="app-header__logo">
          <span class="app-header__logo-mark">B2B</span>
          <span class="app-header__logo-text">Sourcing Hub</span>
        </router-link>

        <nav class="app-nav" :class="{ 'is-open': mobileNavOpen }">
          <div class="app-nav__item app-nav__item--mega">
            <button type="button" class="app-nav__link">
              Solutions
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" class="app-nav__chevron">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            <div class="app-mega">
              <div class="app-mega__inner">
                <div class="app-mega__directions">
                  <span class="app-mega__eyebrow">Directions</span>
                  <button v-for="d in solutionDirections" :key="d.key" type="button" class="app-mega__direction"
                    :class="{ 'is-active': activeSolution === d.key }" @mouseenter="activeSolution = d.key">
                    <span class="app-mega__direction-icon">{{ d.icon }}</span>
                    <span class="app-mega__direction-text">
                      <strong>{{ d.title }}</strong>
                      <span>{{ d.desc }}</span>
                    </span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" class="app-mega__direction-arrow">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>

                <!-- Middle: items for the hovered direction -->
                <div class="app-mega__main">
                  <span class="app-mega__eyebrow">Solutions for {{ currentSolution.title }}</span>
                  <div class="app-mega__grid">
                    <router-link v-for="item in currentSolution.items" :key="item.label" :to="{ name: 'Home' }"
                      class="app-mega__item">
                      <span class="app-mega__item-icon"></span>
                      {{ item.label }}
                      <span v-if="item.badge" class="app-mega__item-badge">{{ item.badge }}</span>
                    </router-link>
                  </div>

                  <div class="app-mega__footer">
                    <div class="app-mega__footer-text">
                      <strong>Need a custom solution?</strong>
                      <span>For teams of 300+ with advanced security, control, and support.</span>
                    </div>
                    <router-link :to="{ name: 'Home' }" class="app-mega__cta">Talk to sales</router-link>
                  </div>
                </div>
                <div class="app-mega__preview">
                  <div class="app-mega__preview-media">
                    <button type="button" class="app-mega__play" aria-label="Play preview">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7L8 5z" />
                      </svg>
                    </button>
                  </div>
                  <strong class="app-mega__preview-title">Learn more about the platform</strong>
                  <ul class="app-mega__preview-list">
                    <li>Verified partner network</li>
                    <li>End-to-end traceability</li>
                    <li>Built for food &amp; manufacturing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="app-nav__item app-nav__item--dropdown">
            <button type="button" class="app-nav__link">
              Resources
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" class="app-nav__chevron">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div class="app-nav__dropdown">
              <router-link :to="{ name: 'Home' }" class="app-nav__dropdown-item">
                <strong>Documentation</strong>
                <span>Guides for getting set up.</span>
              </router-link>
              <router-link :to="{ name: 'Home' }" class="app-nav__dropdown-item">
                <strong>Blog</strong>
                <span>News, case studies, and updates.</span>
              </router-link>
              <router-link :to="{ name: 'Home' }" class="app-nav__dropdown-item">
                <strong>Support Center</strong>
                <span>Get help from our team.</span>
              </router-link>
            </div>
          </div>
          <router-link :to="{ name: 'SearchNetwork' }" class="app-nav__link app-nav__link--plain">
            Search Network
          </router-link>
          <router-link :to="{ name: 'Home' }" class="app-nav__link app-nav__link--plain">About Us</router-link>
        </nav>

        <div class="app-header__actions">
          <a v-if="isLoggedIn" href="#" @click.prevent="logout" class="app-header__signin">
            Log Out
          </a>

          <router-link v-if="!isLoggedIn" :to="{ name: 'SupplierLogin' }" class="app-header__support">
            Get Started
          </router-link>

          <router-link v-else :to="{ name: 'Profile' }" class="app-header__profile">
            <span class="app-header__profile-avatar"><i class="fa fa-user"></i></span>
            My Profile
          </router-link>
         <router-link v-if="isLoggedIn" :to="{ name: 'Conversations' }" class="app-header__chat" aria-label="Messages">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.9-.94L3 20l1.06-3.9A8.4 8.4 0 0 1 3 11.5 8.5 8.5 0 0 1 20.5 8" />
          </svg>
          <span v-if="chatState.unreadCount > 0" class="app-header__chat-badge">
            {{ chatState.unreadCount > 9 ? '9+' : chatState.unreadCount }}
          </span>
        </router-link>
          <button type="button" class="app-header__burger" @click="mobileNavOpen = !mobileNavOpen"
            aria-label="Toggle menu">
            <span></span><span></span><span></span>
          </button>
        </div>
      </div>
    </header>
    <main class="app-main">
      <router-view />
    </main>
  <footer class="app-footer">
  <div class="app-footer__inner">
    <div class="app-footer__main">
      <div class="app-footer__brand">
        <div class="app-footer__logo">
          <span class="app-header__logo-mark app-header__logo-mark--footer">B2B</span>
          <span class="app-footer__logo-text">Sourcing Hub</span>
        </div>
        <p class="app-footer__tagline">
          Connecting suppliers, manufacturers, and service providers across the supply chain.
        </p>
      </div>

      <nav class="app-footer__links">
        <router-link :to="{ name: 'SearchNetwork' }" class="app-footer__link">Search Network</router-link>
        <router-link :to="{ name: 'Home' }" class="app-footer__link">About Us</router-link>
        <router-link :to="{ name: 'Home' }" class="app-footer__link">Support</router-link>
      </nav>
    </div>

    <div class="app-footer__bottom">
      <span class="app-footer__copyright">© {{ year }} B2B Sourcing Hub. All rights reserved.</span>
      <div class="app-footer__legal">
        <router-link :to="{ name: 'Home' }" class="app-footer__legal-link">Privacy Policy</router-link>
        <router-link :to="{ name: 'Home' }" class="app-footer__legal-link">Terms of Service</router-link>
      </div>
    </div>
  </div>
</footer>
  </div>
</template>
<script>
import { chatState, unsubscribeFromAll, startGlobalPolling, stopGlobalPolling } from '@/utils/chatStore'

export default {
  name: 'Front-Layout',
  data() {
    return {
     chatState,
      scrolled: false,
      mobileNavOpen: false,
      year: new Date().getFullYear(),
      activeSolution: 'suppliers',
      isLoggedIn: !!localStorage.getItem('supplier_id'),
      solutionDirections: [
        {
          key: 'suppliers',
          icon: '🌾',
          title: 'Suppliers',
          desc: 'List your catalog and get discovered',
          items: [
            { label: 'Raw Materials' },
            { label: 'Packaging' },
            { label: 'Ingredients' },
            { label: 'Private Label', badge: 'New' },
            { label: 'Co-Packing' },
            { label: 'Logistics Partners' },
          ],
        },
        {
          key: 'manufacturers',
          icon: '🏭',
          title: 'Manufacturers',
          desc: 'Source materials from trusted vendors',
          items: [
            { label: 'Contract Manufacturing' },
            { label: 'Private Label Production' },
            { label: 'Co-Manufacturing' },
            { label: 'Quality Certifications' },
            { label: 'Custom Formulation' },
            { label: 'Capacity Planning' },
          ],
        },
        {
          key: 'service_providers',
          icon: '🤝',
          title: 'Service Providers',
          desc: 'Offer logistics, audits, and more',
          items: [
            { label: 'Logistics & Freight' },
            { label: 'QA Audits' },
            { label: 'Cold Chain' },
            { label: 'Warehousing' },
            { label: 'Compliance Consulting', badge: 'New' },
            { label: 'Broker Services' },
          ],
        },
      ],
    };
  },
 
  computed: {
    currentSolution() {
      return (
        this.solutionDirections.find((d) => d.key === this.activeSolution) || this.solutionDirections[0]
      )
    },
  },

  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.isLoggedIn = !!localStorage.getItem('supplier_id')

if (this.isLoggedIn) {
  const supplierId = Number(localStorage.getItem('supplier_id'))
  startGlobalPolling(supplierId, () => chatState.activeConversationId)
}
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    stopGlobalPolling()
    unsubscribeFromAll()
  },

  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 8
    },

    logout() {
      localStorage.removeItem('supplier_id')
      localStorage.removeItem('company_id')
          localStorage.removeItem('supplier_token')
              // localStorage.removeItem('admin_token')
      stopGlobalPolling()
      this.isLoggedIn = false
      this.$router.push({ name: 'Home' })
    },
  },
};
</script>
<style scoped>
.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.app-shell * {
  box-sizing: border-box;
}

.app-topbar {
  width: 100%;
  background: var(--color-text-dark);
}

.app-topbar__inner {
  max-width: 1200px;
  margin: 0 auto;
  height: 36px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
}

.app-topbar__badge {
  padding: 3px 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.app-topbar__text {
  font-size: 12.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.app-topbar__arrow {
  width: 14px;
  height: 14px;
  color: rgba(255, 255, 255, 0.6);
  transition: transform 0.15s ease;
}

.app-topbar__inner:hover .app-topbar__arrow {
  transform: translateX(2px);
}

/* ==========================================================================
   HEADER
   ========================================================================== */
.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  transition: box-shadow 0.2s ease;
}

.app-header.is-scrolled {
  box-shadow: 0 4px 12px -8px rgba(12, 18, 83, 0.18);
}

.app-header__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 32px;
}

.app-header__logo {
  display: flex;
  align-items: center;
  gap: 9px;
  text-decoration: none;
  flex-shrink: 0;
}

.app-header__logo-mark {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  background: var(--color-primary);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: -0.2px;
}

.app-header__logo-text {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-dark);
  white-space: nowrap;
}

/* Nav */
.app-nav {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
}

.app-nav__item {
  position: relative;
}

.app-nav__link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 64px;
  padding: 0 13px;
  border: none;
  background: none;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--color-text-dark);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.16s ease;
}

.app-nav__link:hover,
.app-nav__link.router-link-active {
  color: var(--color-primary);
}

.app-nav__chevron {
  width: 13px;
  height: 13px;
  color: var(--color-text-muted);
  transition: transform 0.16s ease;
}

.app-nav__item:hover .app-nav__chevron {
  transform: rotate(180deg);
}

/* ---- Simple dropdown (Resources) ---- */
.app-nav__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 260px;
  padding: 6px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 0;
  box-shadow: 0 16px 32px -18px rgba(12, 18, 83, 0.22);
  opacity: 0;
  visibility: hidden;
  transform: translateY(4px);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
}

.app-nav__item--dropdown:hover .app-nav__dropdown {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.app-nav__dropdown-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  text-decoration: none;
  border-radius: 0;
  transition: background 0.14s ease;
}

.app-nav__dropdown-item:hover {
  background: var(--color-tint-light);
}

.app-nav__dropdown-item strong {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.app-nav__dropdown-item span {
  font-size: 12px;
  color: var(--color-text-muted);
  line-height: 1.4;
}

.app-mega {
  position: absolute;
  top: 100%;
  left: 170px;
  transform: translate(-50%, 6px);
  width: 860px;
  max-width: calc(100vw - 32px);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 0;
  box-shadow: 0 24px 48px -20px rgba(12, 18, 83, 0.26);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
  z-index: 50;
}

.app-nav__item--mega:hover .app-mega {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, 0);
}

.app-mega__inner {
  display: grid;
  grid-template-columns: 240px 1fr 220px;
}

.app-mega__eyebrow {
  display: block;
  padding: 16px 20px 10px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

/* Left rail: directions */
.app-mega__directions {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--color-border);
  padding-bottom: 10px;
}

.app-mega__direction {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 20px;
  border: none;
  background: none;
  border-left: 2px solid transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.14s ease, border-color 0.14s ease;
}

.app-mega__direction:hover,
.app-mega__direction.is-active {
  background: var(--color-tint-light);
  border-left-color: var(--color-primary);
}

.app-mega__direction-icon {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.app-mega__direction-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.app-mega__direction-text strong {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.app-mega__direction-text span {
  font-size: 11.5px;
  color: var(--color-text-muted);
  line-height: 1.4;
}

.app-mega__direction-arrow {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  margin-top: 3px;
  color: var(--color-text-muted);
  opacity: 0;
  transform: translateX(-3px);
  transition: opacity 0.14s ease, transform 0.14s ease;
}

.app-mega__direction:hover .app-mega__direction-arrow,
.app-mega__direction.is-active .app-mega__direction-arrow {
  opacity: 1;
  transform: translateX(0);
  color: var(--color-primary);
}

/* Middle: item grid for the active direction */
.app-mega__main {
  padding-bottom: 16px;
  border-right: 1px solid var(--color-border);
}

.app-mega__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 8px;
  padding: 0 12px;
}

.app-mega__item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-dark);
  text-decoration: none;
  border-radius: 0;
  transition: background 0.14s ease;
}

.app-mega__item:hover {
  background: var(--color-tint-light);
  color: var(--color-primary);
}

.app-mega__item-icon {
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  background: var(--color-tint-warm);
}

.app-mega__item-badge {
  margin-left: auto;
  padding: 2px 6px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--color-primary);
  background: var(--color-tint-light);
  border-radius: 0;
}

.app-mega__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 14px 20px 0;
  padding-top: 14px;
  border-top: 1px solid var(--color-border);
}

.app-mega__footer-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.app-mega__footer-text strong {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.app-mega__footer-text span {
  font-size: 11.5px;
  color: var(--color-text-muted);
}

.app-mega__cta {
  flex-shrink: 0;
  height: 32px;
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  border: 1px solid var(--color-border);
  border-radius: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-dark);
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.14s ease, border-color 0.14s ease;
}

.app-mega__cta:hover {
  background: var(--color-tint-light);
  border-color: var(--color-primary);
}

/* Right: preview panel */
.app-mega__preview {
  padding: 16px 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.app-mega__preview-media {
  position: relative;
  height: 90px;
  background: var(--color-text-dark);
  display: grid;
  place-items: center;
}

.app-mega__play {
  width: 34px;
  height: 34px;
  border: none;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  border-radius: 0;
  transition: background 0.15s ease;
}

.app-mega__play svg {
  width: 15px;
  height: 15px;
}

.app-mega__play:hover {
  background: rgba(255, 255, 255, 0.24);
}

.app-mega__preview-title {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.app-mega__preview-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.app-mega__preview-list li {
  position: relative;
  padding-left: 14px;
  font-size: 11.5px;
  color: var(--color-text-muted);
  line-height: 1.4;
}

.app-mega__preview-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 5px;
  width: 6px;
  height: 6px;
  background: var(--color-primary);
}

/* Right-side actions */
.app-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.app-header__signin {
  height: 36px;
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-dark);
  text-decoration: none;
  transition: color 0.15s ease;
}

.app-header__signin:hover {
  color: var(--color-primary);
}

.app-header__support {
  height: 36px;
  display: inline-flex;
  align-items: center;
  padding: 0 18px;
  border-radius: 0;
  background: var(--color-primary);
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.15s ease;
}

.app-header__support:hover {
  background: var(--color-secondary);
}

/* Mobile burger — hidden on desktop */
.app-header__burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
}

.app-header__burger span {
  width: 100%;
  height: 2px;
  background: var(--color-text-dark);
  border-radius: 0;
}

.app-main {
  flex: 1;
}
/* ==========================================================================
   FOOTER — warm-tinted, CTA strip + minimal link row
   ========================================================================== */
.app-footer {
  background: var(--color-tint-light);
  border-top: 1px solid var(--color-border);
}

/* CTA strip */
.app-footer__cta {
  background: var(--color-primary);
  padding: 40px 24px;
}

.app-footer__cta-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.app-footer__cta-title {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.3px;
}

.app-footer__cta-desc {
  display: block;
  margin-top: 4px;
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.72);
}

.app-footer__cta-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 22px;
  border-radius: 10px;
  background: var(--color-tint-warm);
  color: var(--color-primary);
  font-size: 13.5px;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.18s ease;
}
.app-footer__cta-btn svg {
  width: 15px;
  height: 15px;
}
.app-footer__cta-btn:hover {
  transform: translateY(-1px);
}

/* Main footer body */
.app-footer__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px 24px;
}

.app-footer__main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  padding-bottom: 28px;
  border-bottom: 1px solid var(--color-border);
}

.app-footer__brand {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 340px;
}

.app-footer__logo {
  display: flex;
  align-items: center;
  gap: 9px;
}

.app-header__logo-mark--footer {
  background: var(--color-primary);
  color: #ffffff;
}

.app-footer__logo-text {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.app-footer__tagline {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-muted);
}

.app-footer__links {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}

.app-footer__link {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text-dark);
  text-decoration: none;
  position: relative;
  padding-bottom: 3px;
  transition: color 0.15s ease;
}
.app-footer__link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 2px;
  background: var(--color-tint-warm);
  transition: width 0.18s ease;
}
.app-footer__link:hover {
  color: var(--color-primary);
}
.app-footer__link:hover::after {
  width: 100%;
}

.app-footer__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 18px;
}

.app-footer__copyright {
  font-size: 12px;
  color: var(--color-text-muted);
}

.app-footer__legal {
  display: flex;
  gap: 20px;
}

.app-footer__legal-link {
  font-size: 12px;
  color: var(--color-text-muted);
  text-decoration: none;
  transition: color 0.15s ease;
}
.app-footer__legal-link:hover {
  color: var(--color-primary);
}

@media (max-width: 640px) {
  .app-footer__cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }
  .app-footer__cta-btn {
    width: 100%;
    justify-content: center;
  }
  .app-footer__main {
    flex-direction: column;
    gap: 18px;
  }
  .app-footer__links {
    gap: 18px;
  }
  .app-footer__bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 980px) {
  .app-mega {
    position: static;
    width: 100%;
    max-width: none;
    transform: none;
    border: none;
    box-shadow: none;
    display: none;
  }

  .app-nav__item--mega:hover .app-mega {
    display: block;
    opacity: 1;
    visibility: visible;
  }

  .app-mega__inner {
    grid-template-columns: 1fr;
  }

  .app-mega__directions,
  .app-mega__main {
    border-right: none;
    border-bottom: 1px solid var(--color-border);
  }

  .app-mega__preview {
    display: none;
  }
}

@media (max-width: 900px) {
  .app-nav {
    position: fixed;
    top: 100px;
    left: 20px;
    right: 0;
    bottom: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: var(--color-surface);
    padding: 8px 16px;
    transform: translateX(-100%);
    transition: transform 0.2s ease;
    overflow-y: auto;
  }

  .app-nav.is-open {
    transform: translateX(0);
  }

  .app-nav__link {
    height: auto;
    padding: 14px 4px;
    justify-content: space-between;
    border-bottom: 1px solid var(--color-border);
  }

  .app-nav__dropdown {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    box-shadow: none;
    border: none;
    padding: 0 0 8px 12px;
    display: none;
  }

  .app-nav__item--dropdown:hover .app-nav__dropdown {
    display: flex;
    flex-direction: column;
  }

  .app-header__signin {
    display: none;
  }

  .app-header__burger {
    display: flex;
  }

  .app-footer__top {
    grid-template-columns: 1fr 1fr;
    row-gap: 28px;
  }

  .app-footer__brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 560px) {
  .app-header__inner {
    padding: 0 16px;
    gap: 16px;
  }

  .app-header__logo-text {
    display: none;
  }

  .app-topbar__text {
    display: none;
  }

  .app-footer__top {
    grid-template-columns: 1fr;
  }

  .app-footer__bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}

.app-header__profile {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px 0 6px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-dark);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: border-color 0.18s ease, background 0.18s ease;
}

.app-header__profile:hover {
  background: var(--color-tint-light);
  border-color: var(--color-secondary);
}

.app-header__profile-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  font-weight: 700;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .app-header__profile span:not(.app-header__profile-avatar) {
    display: none;
  }

  .app-header__profile {
    padding: 0;
    width: 38px;
    justify-content: center;
  }
}

  .app-header__chat {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    border: 1px solid var(--color-border);
    background: var(--color-surface);
    color: var(--color-text-dark);
    transition: background 0.18s ease, border-color 0.18s ease;
  }
  .app-header__chat svg {
    width: 18px;
    height: 18px;
  }
  .app-header__chat:hover {
    background: var(--color-tint-light);
    border-color: var(--color-secondary);
  }
  .app-header__chat {
  position: relative;
}
.app-header__chat-badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #dc2626;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
</style>